import commaNumber from 'comma-number';
import { useMemo } from 'react';

import Tooltip from '@/components/Tooltip/TooltipV2';
import { MainText } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import CompanyDropdown from '@/features/Account/components/CompanyDropdown';
import useCompanyStore, { COMPANY_PAGE_SIZE } from '@/features/Account/store';
import useAgentStore from '@/features/Agent/stores';
import AttrSelectDropdown from '@/features/Commons/Dropdown/AttrSelectDropdown';
import SentimentBadge from '@/features/Feedback/field/Sentiment/SentimentBadge';
import useColor from '@/hooks/domain/useColor';
import useCustomerAttributes from '@/hooks/domain/useCustomerAttributes';
import useCustomerStore from '@/stores/CustomerStore';
import useColumnConfigStore from '@/stores/DataTable/ColumnConfigStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { EllipsisDiv } from '@/styles/style';
import { CONTACT_COLUMNS } from '@/types/filterKeyConst';
import { readableFormat } from '@/utils/time';
import { DEFAULT_HIGHLIGHT_TARGET, formatNumber } from '@/utils/util';

const useCustomerHeader = (): DataTableColumnT[] => {
  const fetchCompanyList = useCompanyStore((state) => state.fetchCompanyList);
  const { contact_view } = useColumnConfigStore((state) => state.columnConfigs);
  const {
    jobFunctionOptions,
    countryOptions,
    typeOptions,
    agentOptions,
    planOptions,
    stageOptions,
  } = useCustomerAttributes();

  const defaultColumns: DataTableColumnT[] = useMemo(() => {
    return [
      {
        columnKey: 'space',
        columnName: 'Space',
        hiddenColumnName: true,
        isDefaultColumn: true,
        width: 16,
        visible: true,
      },
      {
        columnKey: CONTACT_COLUMNS.NAME,
        columnName: 'Name',
        isDefaultColumn: true,
        orderType: 'string',
        sortKey: CONTACT_COLUMNS.NAME,
        orderable: true,
        width: 200,
        visible: true,
      },
      {
        columnKey: CONTACT_COLUMNS.ACCOUNT,
        columnName: 'Account',
        width: 150,
        visible: false,
        searchable: true,
        filterable: true,
        filterList: [],
        asyncData: {
          fetchList: async (prev, currPage, page, searchValue) => {
            const res = await fetchCompanyList({
              prevData: prev,
              page,
              searchValue,
            });
            const list = res.map((company) => ({
              columnKey: CONTACT_COLUMNS.ACCOUNT,
              filterKey: company.alias,
              filterName: company.name,
              filterOptionNode: company.name,
              paginate_token: company.paginate_token,
            }));
            return {
              page,
              hasNextPage: !!res[COMPANY_PAGE_SIZE - 1],
              list,
            };
          },
        },
      },
      {
        columnKey: CONTACT_COLUMNS.SENTIMENT,
        columnName: 'Recent sentiment',
        getHeaderNode() {
          return (
            <Tooltip tooltipNode="The key sentiment from the most recent customer interaction.">
              <span>Recent sentiment</span>
            </Tooltip>
          );
        },
        width: 200,
        visible: true,
      },
      {
        columnKey: CONTACT_COLUMNS.JOB_FUNCTION,
        columnName: 'Job function',
        width: 100,
        visible: false,
        filterable: true,
        searchable: true,
        filterList: jobFunctionOptions.map((position) => ({
          columnKey: CONTACT_COLUMNS.JOB_FUNCTION,
          filterKey: position.value,
          filterName: position.value as string,
          filterOptionNode: position.value,
        })),
      },
      {
        columnKey: CONTACT_COLUMNS.EMAIL,
        columnName: 'Email',
        width: 300,
        visible: false,
      },
      {
        columnKey: CONTACT_COLUMNS.PHONE,
        columnName: 'Phone Number',
        width: 200,
        visible: false,
      },
      {
        columnKey: CONTACT_COLUMNS.DESC,
        columnName: 'Description',
        width: 400,
        visible: false,
      },
      {
        columnKey: CONTACT_COLUMNS.REFERENCE_LINK,
        columnName: 'Reference link',
        width: 400,
        visible: false,
      },
      {
        columnKey: CONTACT_COLUMNS.COUNTRY,
        columnName: 'Country',
        width: 250,
        visible: false,
        filterable: true,
        searchable: true,
        filterList: countryOptions.map((option) => ({
          columnKey: CONTACT_COLUMNS.COUNTRY,
          filterKey: option.value,
          filterName: option.searchName,
          filterOptionNode: option.searchName,
        })),
      },
      {
        columnKey: CONTACT_COLUMNS.TYPE,
        columnName: 'Type',
        width: 250,
        visible: false,
        filterable: true,
        searchable: true,
        filterList: typeOptions.map((option) => ({
          columnKey: CONTACT_COLUMNS.TYPE,
          filterKey: option.value,
          filterName: option.searchName,
          filterOptionNode: option.searchName,
        })),
      },
      {
        columnKey: CONTACT_COLUMNS.PLAN,
        columnName: 'Plan',
        width: 250,
        visible: false,
        filterable: true,
        filterList: planOptions.map((option) => ({
          columnKey: CONTACT_COLUMNS.PLAN,
          filterKey: option.value,
          filterName: option.searchName,
          filterOptionNode: option.searchName,
        })),
      },
      {
        columnKey: CONTACT_COLUMNS.STAGE,
        columnName: 'Stage',
        width: 250,
        visible: false,
        filterable: true,
        filterList: stageOptions.map((option) => ({
          columnKey: CONTACT_COLUMNS.STAGE,
          filterKey: option.value,
          filterName: option.searchName,
          filterOptionNode: option.searchName,
        })),
      },
      {
        columnKey: CONTACT_COLUMNS.MRR,
        columnName: 'Monthly spend',
        width: 250,
        visible: false,
        sortKey: CONTACT_COLUMNS.MRR,
        orderable: true,
        orderType: 'number',
      },
      {
        columnKey: CONTACT_COLUMNS.MANAGER,
        columnName: 'Agent',
        width: 250,
        visible: false,
        filterable: true,
        searchable: true,
        filterList: agentOptions.map((option) => ({
          columnKey: CONTACT_COLUMNS.MANAGER,
          filterKey: option.value,
          filterName: option.searchName,
          filterOptionNode: option.searchName,
        })),
      },
      {
        columnKey: CONTACT_COLUMNS.EXTERNAL_ID,
        columnName: 'ID',
        width: 200,
        visible: false,
      },
      {
        columnKey: CONTACT_COLUMNS.SNS_ID,
        columnName: 'SNS ID',
        width: 200,
        visible: false,
      },
      {
        columnKey: CONTACT_COLUMNS.USAGE,
        columnName: 'Usage',
        orderType: 'number',
        sortKey: CONTACT_COLUMNS.USAGE,
        orderable: true,
        width: 200,
        visible: false,
      },
      {
        columnKey: CONTACT_COLUMNS.FEEDBACK_COUNT,
        columnName: 'Feedback',
        width: 150,
        visible: false,
      },
      {
        columnKey: CONTACT_COLUMNS.NPS,
        columnName: 'NPS Score',
        width: 150,
        visible: false,
      },
      {
        columnKey: CONTACT_COLUMNS.CES,
        columnName: 'CES Score',
        width: 150,
        visible: false,
      },
      {
        columnKey: CONTACT_COLUMNS.CSAT,
        columnName: 'CSAT Score',
        width: 150,
        visible: false,
      },
      {
        columnKey: CONTACT_COLUMNS.LAST_ACTIVITY,
        columnName: 'Last activity',
        width: 130,
        visible: false,
      },
      {
        columnKey: CONTACT_COLUMNS.DATE,
        columnName: 'Date',
        width: 130,
        visible: false,
      },
    ];
  }, [
    jobFunctionOptions,
    countryOptions,
    typeOptions,
    planOptions,
    stageOptions,
    agentOptions,
    fetchCompanyList,
  ]);

  const columns = useMemo(() => {
    const fullColumns = [...defaultColumns];
    contact_view.forEach((config) => {
      const targetIndex = fullColumns.findIndex(
        (col) => col.columnKey === config.name,
      );
      if (targetIndex === -1) return;
      fullColumns[targetIndex] = {
        ...fullColumns[targetIndex],
        width: config.width,
        visible: config.visible,
        columnOrderIndex: config.order,
      };
    });
    return fullColumns;
  }, [contact_view, defaultColumns]);

  return columns;
};

const useCustomerTableData = (
  data: PaginatedData<CustomerT>[],
): DataTableRowT[] => {
  const { customerMap, setSelectedCustomer, changeCustomerAttr } =
    useCustomerStore(
      ({ customerMap, setSelectedCustomer, changeCustomerAttr }) => ({
        customerMap,
        setSelectedCustomer,
        changeCustomerAttr,
      }),
    );

  const color = useColor();
  const agentMap = useAgentStore((state) => state.agentMap);

  const {
    jobFunctionOptions,
    countryOptions,
    typeOptions,
    agentOptions,
    planOptions,
    stageOptions,
  } = useCustomerAttributes();
  const { updateWorkspaceFieldOptions } = useWorkspaceStore(
    ({ updateWorkspaceFieldOptions }) => ({ updateWorkspaceFieldOptions }),
  );

  const result: DataTableRowT[] = useMemo(() => {
    return data.reduce((acc, value) => {
      const customer = customerMap[value.alias];
      if (!customer) return acc;
      acc.push({
        onRowClick: () => setSelectedCustomer(customer.alias),
        rowKey: customer.alias,
        rowData: [
          {
            columnKey: 'space',
            children: <div />,
          },
          {
            columnKey: CONTACT_COLUMNS.NAME,
            children: (
              <Typo
                type="label2"
                color={color.gray700}
                className={DEFAULT_HIGHLIGHT_TARGET}
                ellipsis
                style={{ width: 200 }}
              >
                {customer.name}
              </Typo>
            ),
            onCellClick: () => setSelectedCustomer(customer.alias),
            rawData: customer.name,
          },
          {
            columnKey: CONTACT_COLUMNS.ACCOUNT,
            children: (
              <CompanyDropdown
                name={`customer-list-${customer.alias}-company-dropdown`}
                company_info={
                  (customer.company_info && {
                    name: customer.company_info?.name,
                    alias: customer.company_info.alias,
                  }) ||
                  null
                }
                changeCompany={(company) => {
                  changeCustomerAttr(
                    customer.alias,
                    'company_info',
                    company
                      ? {
                          name: company.name,
                          alias: company.alias,
                        }
                      : undefined,
                  );
                }}
                view="list"
              />
            ),
            rawData: customer.company || '',
          },
          {
            columnKey: CONTACT_COLUMNS.SENTIMENT,
            children: (
              <EllipsisDiv>
                {customer.latest_sentiment && (
                  <Tooltip tooltipNode="The key sentiment from the most recent customer interaction.">
                    <SentimentBadge type={customer.latest_sentiment} />
                  </Tooltip>
                )}
              </EllipsisDiv>
            ),
            rawData: customer.latest_sentiment || '',
          },
          {
            columnKey: CONTACT_COLUMNS.JOB_FUNCTION,
            children: (
              <AttrSelectDropdown
                name={`customer-list-${customer.alias}-position-dropdown`}
                view="list"
                selected={customer.job_function || null}
                options={jobFunctionOptions}
                onSelect={(key) =>
                  changeCustomerAttr(
                    customer.alias,
                    'job_function',
                    key,
                  )
                }
                onClear={() =>
                  changeCustomerAttr(customer.alias, 'job_function', undefined)
                }
                onCreate={async (key) => {
                  await updateWorkspaceFieldOptions(1, 'job_function', key);
                  changeCustomerAttr(
                    customer.alias,
                    'job_function',
                    key,
                  );
                }}
                onDelete={(key) =>
                  updateWorkspaceFieldOptions(
                    4,
                    'job_function',
                    key.value as string,
                  )
                }
              />
            ),
            rawData: customer.job_function || 'Not assigned',
          },
          {
            columnKey: CONTACT_COLUMNS.EMAIL,
            children: (
              <EllipsisDiv>
                <Typo
                  type="label2"
                  color={color.gray700}
                  className={DEFAULT_HIGHLIGHT_TARGET}
                >
                  {customer.email}
                </Typo>
              </EllipsisDiv>
            ),
            rawData: customer.email || '',
          },
          {
            columnKey: CONTACT_COLUMNS.PHONE,
            children: (
              <EllipsisDiv>
                <Typo type="label2" color={color.gray700}>
                  {customer.phone}
                </Typo>
              </EllipsisDiv>
            ),
            rawData: customer.phone || '',
          },
          {
            columnKey: CONTACT_COLUMNS.DESC,
            children: (
              <EllipsisDiv>
                <Typo type="label2" color={color.gray700}>
                  {customer.desc}
                </Typo>
              </EllipsisDiv>
            ),
            rawData: customer.desc || '',
          },
          {
            columnKey: CONTACT_COLUMNS.REFERENCE_LINK,
            children: (
              <EllipsisDiv>
                <Typo type="label2" color={color.gray700}>
                  {customer.reference_link}
                </Typo>
              </EllipsisDiv>
            ),
            rawData: customer.reference_link || '',
          },
          {
            columnKey: CONTACT_COLUMNS.COUNTRY,
            children: (
              <AttrSelectDropdown
                name={`customer-list-${customer.alias}-country-dropdown`}
                view="list"
                selected={customer.country || null}
                options={countryOptions}
                onSelect={(key) =>
                  changeCustomerAttr(customer.alias, 'country', key)
                }
                onClear={() =>
                  changeCustomerAttr(customer.alias, 'country', undefined)
                }
              />
            ),
            rawData: customer.country || 'Not assigned',
          },
          {
            columnKey: CONTACT_COLUMNS.TYPE,
            children: (
              <AttrSelectDropdown
                name={`customer-list-${customer.alias}-type-dropdown`}
                view="list"
                selected={customer.type || null}
                options={typeOptions}
                onSelect={(key) =>
                  changeCustomerAttr(customer.alias, 'type', key)
                }
                onClear={() =>
                  changeCustomerAttr(customer.alias, 'type', undefined)
                }
                onCreate={async (key) => {
                  await updateWorkspaceFieldOptions(1, 'contact_type', key);
                  changeCustomerAttr(customer.alias, 'type', key);
                }}
                onDelete={(key) =>
                  updateWorkspaceFieldOptions(
                    4,
                    'contact_type',
                    key.value as string,
                  )
                }
              />
            ),
            rawData: customer.type || 'Not assigned',
          },
          {
            columnKey: CONTACT_COLUMNS.PLAN,
            children: (
              <AttrSelectDropdown
                name={`customer-list-${customer.alias}-plan-dropdown`}
                view="list"
                selected={customer.contact_plan || null}
                options={planOptions}
                onSelect={(key) =>
                  changeCustomerAttr(
                    customer.alias,
                    'contact_plan',
                    key,
                  )
                }
                onClear={() =>
                  changeCustomerAttr(customer.alias, 'contact_plan', undefined)
                }
                onCreate={async (key) => {
                  await updateWorkspaceFieldOptions(1, 'contact_plan', key);
                  changeCustomerAttr(
                    customer.alias,
                    'contact_plan',
                    key,
                  );
                }}
                onDelete={(key) =>
                  updateWorkspaceFieldOptions(
                    4,
                    'contact_plan',
                    key.value as string,
                  )
                }
              />
            ),
          },
          {
            columnKey: CONTACT_COLUMNS.STAGE,
            children: (
              <AttrSelectDropdown
                name={`customer-list-${customer.alias}-stage-dropdown`}
                view="list"
                selected={customer.contact_stage || null}
                options={stageOptions}
                onSelect={(key) =>
                  changeCustomerAttr(
                    customer.alias,
                    'contact_stage',
                    key,
                  )
                }
                onClear={() =>
                  changeCustomerAttr(customer.alias, 'contact_stage', undefined)
                }
                onCreate={async (key) => {
                  await updateWorkspaceFieldOptions(1, 'contact_stage', key);
                  changeCustomerAttr(
                    customer.alias,
                    'contact_stage',
                    key,
                  );
                }}
                onDelete={(key) =>
                  updateWorkspaceFieldOptions(
                    4,
                    'contact_stage',
                    key.value as string,
                  )
                }
              />
            ),
          },
          {
            columnKey: CONTACT_COLUMNS.MRR,
            children: (
              <MainText>
                {customer.mrr ? commaNumber(parseInt(`${customer.mrr}`)) : 0}
              </MainText>
            ),
          },
          {
            columnKey: CONTACT_COLUMNS.EXTERNAL_ID,
            children: (
              <EllipsisDiv>
                <Typo type="label2" color={color.gray700}>
                  {customer.external_id}
                </Typo>
              </EllipsisDiv>
            ),
            rawData: customer.external_id || '',
          },
          {
            columnKey: CONTACT_COLUMNS.SNS_ID,
            children: (
              <EllipsisDiv>
                <Typo type="label2" color={color.gray700}>
                  {customer.sns_id}
                </Typo>
              </EllipsisDiv>
            ),
            rawData: customer.sns_id || '',
          },
          {
            columnKey: CONTACT_COLUMNS.USAGE,
            children: (
              <EllipsisDiv>
                <Typo type="label2" color={color.gray700}>
                  {customer.usage
                    ? formatNumber(customer.usage)
                    : customer.usage}
                </Typo>
              </EllipsisDiv>
            ),
            rawData: customer.usage || '',
          },
          {
            columnKey: CONTACT_COLUMNS.MANAGER,
            children: (
              <AttrSelectDropdown
                name={`customer-list-${customer.alias}-agent-dropdown`}
                view="list"
                selected={
                  customer.agent ? agentMap[customer.agent]?.name || null : null
                }
                options={agentOptions}
                onSelect={(key) =>
                  changeCustomerAttr(customer.alias, 'agent', key)
                }
                onClear={() =>
                  changeCustomerAttr(customer.alias, 'agent', undefined)
                }
              />
            ),
            rawData: customer.agent || 'Not assigned',
          },
          {
            columnKey: CONTACT_COLUMNS.FEEDBACK_COUNT,
            children: (
              <Typo type="label2" color={color.gray700}>
                {customer?.feedback_count || 0}
              </Typo>
            ),
            rawData: customer?.feedback_count || 0,
          },
          {
            columnKey: CONTACT_COLUMNS.NPS,
            children: <MainText>{customer?.nps}</MainText>,
            rawData: customer?.nps,
          },
          {
            columnKey: CONTACT_COLUMNS.CES,
            children: <MainText>{customer?.ces}</MainText>,
            rawData: customer?.ces,
          },
          {
            columnKey: CONTACT_COLUMNS.CSAT,
            children: <MainText>{customer?.csat}</MainText>,
            rawData: customer?.csat,
          },
          {
            columnKey: CONTACT_COLUMNS.LAST_ACTIVITY,
            children: (
              <>
                {customer.latest_activity && (
                  <Typo type="label2" color={color.gray700}>
                    {readableFormat(customer.latest_activity)}
                  </Typo>
                )}
              </>
            ),
            rawData: customer.latest_activity || '',
          },
          {
            columnKey: CONTACT_COLUMNS.DATE,
            children: (
              <>
                {customer.date && (
                  <Typo type="label2" color={color.gray700}>
                    {readableFormat(customer.date)}
                  </Typo>
                )}
              </>
            ),
            rawData: customer.date || '',
          },
        ],
      });
      return acc;
    }, [] as DataTableRowT[]);
  }, [
    data,
    customerMap,
    color.gray700,
    jobFunctionOptions,
    countryOptions,
    typeOptions,
    planOptions,
    stageOptions,
    agentOptions,
    setSelectedCustomer,
    changeCustomerAttr,
    updateWorkspaceFieldOptions,
  ]);

  return result;
};

export { useCustomerHeader, useCustomerTableData };
